import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import SecurityAppicons from "./SecurityAppicons";
import Alerts from "./Alerts";
import AdvisoryComponent from "../User/UserDashboard/AdvisoryComponent";
import Announcements from "../User/Announcements/Announcements";
import AdminQuickAccessMobile from "../AdminMobileMode/AdminQuickAccessMobile";
import AdminHeaderMobile from "../AdminMobileMode/AdminHeaderMobile";
import { queryAllDocsByFieldSnap } from "../../utils/firebase/Firebase.utils";
import { FiAlertTriangle, FiChevronRight, FiX } from "react-icons/fi";

export default function Home() {
  const currentUser = useSelector((state) => state.userObject);
  const [activeEmergencies, setActiveEmergencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEmergencyAlert, setShowEmergencyAlert] = useState(true);

  useEffect(() => {
    // Subscribe to active emergencies
    const unsubscribe = queryAllDocsByFieldSnap(
      "emergencies",
      "status",
      "==",
      "Active",
      (emergencyData) => {
        // Sort by most recent first
        const sortByTimestamp = (a, b) => {
          const dateA = new Date(a.timestamp || a.createdAt || 0);
          const dateB = new Date(b.timestamp || b.createdAt || 0);
          return dateB - dateA;
        };

        setActiveEmergencies(emergencyData.sort(sortByTimestamp));
        setLoading(false);
      }
    );

    // Cleanup subscription on unmount
    return () => unsubscribe && unsubscribe();
  }, []);

  // Helper function to get priority-based styling
  const getPriorityColor = (priority) => {
    switch (priority) {
      case "High":
        return "bg-red-50";
      case "Medium":
        return "bg-orange-50";
      case "Low":
        return "bg-yellow-50";
      default:
        return "bg-gray-50";
    }
  };

  // Helper function to get emergency type icon
  const getEmergencyIcon = (type) => {
    const emergencyTypes = {
      "Fire Alarm": "🔥",
      "Water Leak": "💧",
      "Medical Emergency": "🚑",
      "Security Breach": "🚨",
      "Elevator Malfunction": "⚠️",
    };
    return emergencyTypes[type] || "⚠️";
  };

  // Helper function to get priority badge color
  const getPriorityBadgeColor = (priority) => {
    switch (priority) {
      case "High":
        return "bg-red-600 text-white";
      case "Medium":
        return "bg-orange-500 text-white";
      case "Low":
        return "bg-yellow-500 text-white";
      default:
        return "bg-gray-500 text-white";
    }
  };

  return (
    <div className="py-2 px-1">
      <div className="bg-[#363062] rounded-lg p-5 mb-5 shadow-md relative overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-[#8FCD00] opacity-10 rounded-full -mr-10 -mt-10"></div>
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-[#8FCD00] opacity-10 rounded-full -ml-10 -mb-10"></div>
        <AdminHeaderMobile />
      </div>

      <div className="mt-4 space-y-4">
        {!loading && activeEmergencies.length > 0 && showEmergencyAlert && (
          <div className="mb-4">
            <div className="rounded-xl overflow-hidden shadow-md bg-white">
              {/* Header */}
              <div className="bg-gradient-to-r from-red-600 to-red-500 px-5 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="bg-white bg-opacity-20 p-2 rounded-full mr-3">
                      <FiAlertTriangle className="text-white w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">
                        {activeEmergencies.length === 1
                          ? "Active Emergency"
                          : `${activeEmergencies.length} Active Emergencies`}
                      </h3>
                      <p className="text-white text-opacity-80 text-xs">
                        Immediate attention required
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowEmergencyAlert(false)}
                    className="bg-white bg-opacity-20 p-1.5 rounded-full text-white hover:bg-opacity-30 transition-all"
                  >
                    <FiX className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Emergency List */}
              <div className="px-5 py-3 divide-y divide-gray-100">
                {activeEmergencies.slice(0, 3).map((emergency) => (
                  <div key={emergency.id} className="py-3 first:pt-1 last:pb-1">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <span className="text-2xl">
                          {getEmergencyIcon(emergency.type)}
                        </span>
                        <div>
                          <div
                            className="font-medium"
                            style={{ color: "#363062" }}
                          >
                            {emergency.type} • Unit {emergency.unitNumber}
                          </div>
                          <p className="text-xs mt-0.5 text-gray-500 line-clamp-1">
                            {emergency.details}
                          </p>
                        </div>
                      </div>
                      <span
                        className={`text-xs px-2 py-1 rounded-full font-medium ${getPriorityBadgeColor(
                          emergency.priority
                        )}`}
                      >
                        {emergency.priority}
                      </span>
                    </div>
                  </div>
                ))}

                {activeEmergencies.length > 3 && (
                  <div className="py-2 text-center">
                    <p className="text-sm italic" style={{ color: "#363062" }}>
                      + {activeEmergencies.length - 3} more emergencies
                    </p>
                  </div>
                )}
              </div>

              {/* Action Button */}
              <div className="px-5 py-3 bg-gray-50">
                <Link
                  to="/alerts"
                  className="flex items-center justify-center w-full p-2.5 rounded-lg transition-colors"
                  style={{ backgroundColor: "#363062", color: "white" }}
                >
                  <span>View All Emergencies</span>
                  <FiChevronRight className="ml-1" />
                </Link>
              </div>
            </div>
          </div>
        )}

        <SecurityAppicons />
        <AdminQuickAccessMobile />
        {/* <Alerts />
        <AdvisoryComponent /> */}
        <Announcements />
      </div>
    </div>
  );
}
